import { createElement, type ReactNode } from 'react'
import type { ColumnDef } from '@tanstack/react-table'
import { formatCurrency, formatDate } from '@/lib/utils.ts'
import { StatusBadge } from '../StatusBadge.tsx'
import { PriorityBadge } from '../PriorityBadge.tsx'
import type { Priority } from '@/styles/tokens.ts'

/**
 * Helper-Builder fuer typische Spalten. Reduzieren Boilerplate in Pages.
 */

export function textColumn<TRow>(
  accessor: keyof TRow,
  header: string,
  opts: { size?: number } = {},
): ColumnDef<TRow, unknown> {
  return {
    accessorKey: accessor as string,
    header,
    size: opts.size,
    cell: ({ getValue }) => (getValue() as ReactNode) ?? '—',
  }
}

export function dateColumn<TRow>(
  accessor: keyof TRow,
  header: string,
  opts: { size?: number; withTime?: boolean } = {},
): ColumnDef<TRow, unknown> {
  return {
    accessorKey: accessor as string,
    header,
    size: opts.size,
    cell: ({ getValue }) => {
      const v = getValue() as string | Date | null | undefined
      if (!v) return '—'
      return formatDate(
        v,
        opts.withTime ? { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' } : undefined,
      )
    },
  }
}

export function currencyColumn<TRow>(
  accessor: keyof TRow,
  header: string,
  opts: { size?: number } = {},
): ColumnDef<TRow, unknown> {
  return {
    accessorKey: accessor as string,
    header,
    size: opts.size,
    cell: ({ getValue }) => {
      const v = getValue() as number | null | undefined
      if (v == null) return '—'
      return formatCurrency(v)
    },
  }
}

export function statusColumn<TRow>(
  accessor: keyof TRow,
  header = 'Status',
  opts: { size?: number } = {},
): ColumnDef<TRow, unknown> {
  return {
    accessorKey: accessor as string,
    header,
    size: opts.size,
    cell: ({ getValue }) =>
      createElement(StatusBadge, { status: (getValue() as string | null | undefined) ?? null }),
  }
}

export function priorityColumn<TRow>(
  accessor: keyof TRow,
  header = 'Priorität',
  opts: { size?: number } = {},
): ColumnDef<TRow, unknown> {
  return {
    accessorKey: accessor as string,
    header,
    size: opts.size,
    cell: ({ getValue }) => {
      const p = getValue() as Priority | null | undefined
      if (!p) return '—'
      return createElement(PriorityBadge, { priority: p })
    },
  }
}

export function actionsColumn<TRow>(
  header = '',
  render: (row: TRow) => ReactNode,
  opts: { size?: number } = {},
): ColumnDef<TRow, unknown> {
  return {
    id: '__actions__',
    header,
    enableSorting: false,
    size: opts.size ?? 60,
    cell: ({ row }) => render(row.original),
  }
}
